import { useState, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import AreaService from "../../../../services/area_service";

/**
 * useAreas
 * Hook de lógica para o módulo de Áreas.
 * Utiliza o AreaService para persistência.
 */
export const useAreas = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "name",
        orderDirection: "asc"
    }), []);

    // Paginação das Áreas
    const { data: areas, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/areas`,
        idTenant,
        paginationOptions
    );

    /**
     * Salvar uma Área
     */
    const saveArea = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await AreaService.save(formData, idTenant, user, areas);
            toast.success(formData.id ? "Área atualizada!" : "Nova área cadastrada!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar área:", err);
            toast.error("Erro ao salvar área.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, areas, refresh]);

    /**
     * Excluir uma Área
     */
    const deleteArea = useCallback(async (areaId) => {
        if (!idTenant) return;

        const areaToDelete = areas.find(a => a.id === areaId);

        const confirmed = await ConfirmDialog({
            title: "Excluir Área?",
            text: `Tem certeza que deseja excluir "${areaToDelete?.name || 'esta área'}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            await AreaService.delete(areaId, idTenant, user, areaToDelete?.name);
            toast.success("Área removida!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir área:", err);
            toast.error("Não foi possível excluir a área.");
            return false;
        }
    }, [idTenant, user, areas, refresh]);


    return {
        areas,
        loading,
        error,
        isSaving,
        actions: {
            saveArea,
            deleteArea,
            refresh
        }
    };
};
